/*
 * <dwm-tabs> — generic accessible tabs for DWM sections (dwm-startpoints, dwm-anatomy).
 *
 * Markup contract:
 *   <dwm-tabs>
 *     <div data-tabs-nav role="tablist" hidden> <button data-tab role="tab" id aria-controls> … </div>
 *     <div data-tab-panel id> … </div> (one per tab, same order)
 *   </dwm-tabs>
 *
 * One state: the active index. select() is the only place it changes; it updates the tabs (aria-selected, roving
 * tabindex), the panels (is-active, inert), `data-active` on the element and dispatches `dwm:tab-change`.
 * Click, ArrowLeft/Right/Up/Down, Home and End select a tab. No timers, no autoplay.
 * Without this script the navigation stays hidden and every panel is simply listed.
 * In the Theme Editor, selecting a block inside a panel shows that panel.
 */
class DwmTabs extends HTMLElement {
  #navs = [];
  #tabs = [];
  #panels = [];
  #index = 0;

  get index() {
    return this.#index;
  }

  connectedCallback() {
    this.#navs = [...this.querySelectorAll('[data-tabs-nav]')].filter((nav) => nav.closest('dwm-tabs') === this);
    this.#tabs = [...this.querySelectorAll('[data-tab]')].filter((tab) => tab.closest('dwm-tabs') === this);
    this.#panels = [...this.querySelectorAll('[data-tab-panel]')].filter((panel) => panel.closest('dwm-tabs') === this);

    if (this.#navs.length === 0 || this.#tabs.length < 2 || this.#tabs.length !== this.#panels.length) {
      return;
    }

    this.#panels.forEach((panel, i) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', this.#tabs[i].id);
      panel.setAttribute('tabindex', '0');
    });

    this.#navs.forEach((nav) => {
      nav.removeAttribute('hidden');
      nav.addEventListener('click', this.#onClick);
      nav.addEventListener('keydown', this.#onKeydown);
    });

    this.querySelectorAll('[data-tabs-enhanced-only]').forEach((element) => element.removeAttribute('hidden'));
    this.classList.add('is-enhanced');
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.select(0);
  }

  disconnectedCallback() {
    this.#navs.forEach((nav) => {
      nav.removeEventListener('click', this.#onClick);
      nav.removeEventListener('keydown', this.#onKeydown);
    });
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  select(index, { focus = false } = {}) {
    const count = this.#tabs.length;
    this.#index = ((index % count) + count) % count;

    this.#tabs.forEach((tab, i) => {
      const active = i === this.#index;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.#panels.forEach((panel, i) => {
      const active = i === this.#index;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('inert', !active);
    });

    this.dataset.active = String(this.#index);

    const tab = this.#tabs[this.#index];

    if (focus) {
      tab.focus({ preventScroll: true });
    }

    // A horizontally scrolling tab row keeps the chosen tab in view, without scrolling the page.
    const nav = tab.closest('[data-tabs-nav]');

    if (nav && nav.scrollWidth > nav.clientWidth + 1) {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      nav.scrollTo({ left: tab.offsetLeft - (nav.clientWidth - tab.offsetWidth) / 2, behavior });
    }

    this.dispatchEvent(new CustomEvent('dwm:tab-change', { bubbles: true, detail: { index: this.#index, panel: this.#panels[this.#index] } }));
  }

  #onClick = (event) => {
    const tab = event.target.closest('[data-tab]');

    if (tab && this.#tabs.includes(tab)) {
      this.select(this.#tabs.indexOf(tab));
    }
  };

  #onKeydown = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

    if (!event.target.closest('[data-tab]')) {
      return;
    }

    if (event.key in keys) {
      event.preventDefault();
      this.select(this.#index + keys[event.key], { focus: true });
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.select(event.key === 'Home' ? 0 : this.#tabs.length - 1, { focus: true });
    }
  };

  #onBlockSelect = (event) => {
    const index = this.#panels.findIndex((panel) => panel.contains(event.target) || panel === event.target);

    if (index >= 0) {
      this.select(index);
    }
  };
}

if (!customElements.get('dwm-tabs')) {
  customElements.define('dwm-tabs', DwmTabs);
}
